import { useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getStockBundleById } from '@/lib/queries/bundles'
import { getProductsByBundleId } from '@/lib/queries/products'
import type { StockBundleDetail } from '@/lib/queries/bundles'
import toast from 'react-hot-toast'

/**
 * Bundle Detail page
 * Mobile-first design optimized for iPad Mini
 * Based on wireframe: bundle-detail.html
 */
export default function BundleDetail() {
  const { id } = useParams<{ id: string }>()

  const { data: bundle, isLoading, error } = useQuery({
    queryKey: ['stockBundle', id],
    queryFn: () => getStockBundleById(id!),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  })

  // Fetch products that use this bundle
  const { data: products = [] } = useQuery({
    queryKey: ['productsByBundle', id],
    queryFn: () => getProductsByBundleId(id!),
    enabled: !!id && !!bundle?._id,
    staleTime: 0,
    gcTime: 0,
  })

  // Helper to get category name
  const getCategoryName = (category: any): string => {
    if (!category) return 'Uncategorized'
    if (typeof category === 'object' && 'name' in category) {
      return category.name || 'Uncategorized'
    }
    return 'Uncategorized'
  }

  // Group components by category - MUST be called before any early returns
  const rawComponents = bundle?.stockOrder?.stockBundleStockItems
  const components = useMemo(() => rawComponents ?? [], [rawComponents])
  const groupedComponents = useMemo(() => {
    if (!components || components.length === 0) return new Map<string, typeof components>()

    const grouped = new Map<string, typeof components>()
    
    components.forEach((component) => {
      const category = component.stockItem?.category
      const categoryName = getCategoryName(category)
      
      if (!grouped.has(categoryName)) {
        grouped.set(categoryName, [])
      }
      grouped.get(categoryName)!.push(component)
    })

    // Sort categories alphabetically
    const sortedCategories = Array.from(grouped.keys()).sort()
    const sortedMap = new Map<string, typeof components>()
    sortedCategories.forEach((categoryName) => {
      sortedMap.set(categoryName, grouped.get(categoryName)!)
    })

    return sortedMap
  }, [components])

  // Helper to get status (Published/Draft) based on Zoho sync status
  const getStatus = (bundle: StockBundleDetail): 'published' | 'draft' => {
    if (bundle.zohoMapping?.zohoSyncStatus === 'synced') {
      return 'published'
    }
    return 'draft'
  }

  // Helper to get Zoho status display text
  const getZohoStatusText = (bundle: StockBundleDetail): string => {
    const status = bundle.zohoMapping?.zohoSyncStatus
    switch (status) {
      case 'synced':
        return 'Synced'
      case 'error':
        return 'Error'
      case 'not_synced':
      default:
        return 'Not Published'
    }
  }

  // Helper to format price
  const formatPrice = (price: number | undefined): string => {
    if (price === undefined || price === null) return 'N/A'
    return `£${price.toFixed(2)}`
  }

  // Helper to format date relative time
  const formatRelativeTime = (dateString: string | undefined): string => {
    if (!dateString) return 'Never'
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`
      if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`
      if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`
      return date.toLocaleDateString()
    } catch {
      return 'Unknown'
    }
  }

  // Helper to get component type display (for now, all are "Stock Item" since schema doesn't have componentType)
  const getComponentType = (): string => {
    return 'Stock Item' // TODO: Get from schema if componentType field exists
  }

  // Helper to calculate component total cost
  const getComponentTotal = (quantity: number, price?: number): number => {
    if (price === undefined || price === null) return 0
    return quantity * price
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24 flex items-center justify-center">
        <div className="text-gray-500">Loading bundle...</div>
      </div>
    )
  }

  if (error || !bundle) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Bundle not found</p>
          <Link
            to="/bundles"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium"
          >
            Back to List
          </Link>
        </div>
      </div>
    )
  }

  const status = getStatus(bundle)
  const zohoStatus = getZohoStatusText(bundle)
  const price = bundle.selling?.sellAt ? formatPrice(bundle.selling.sellAt) : 'N/A'
  const stockLevel = bundle.stockOrder?.availableStock ?? 0
  const reorderPoint = bundle.stockOrder?.reorderPoint ?? 0
  const isPublished = status === 'published'
  const lastSync = bundle.zohoMapping?.lastSync

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4 flex justify-between items-center">
          <h1 className="text-xl font-bold text-gray-900">Bundle Detail</h1>
          <div className="flex gap-3">
            <Link
              to={`/bundles/${id}/edit`}
              className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center touch-target-min-44 hover:bg-gray-200 transition-colors"
              aria-label="Edit"
            >
              ✏️
            </Link>
            <button
              className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center touch-target-min-44 hover:bg-gray-200 transition-colors"
              aria-label="More options"
            >
              ⋯
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4">
        {/* Bundle Info Card */}
        <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            {bundle.title || 'Untitled Bundle'}
          </h2>
          <p className="text-sm text-gray-600 mb-3">SKU: {bundle.sku || 'No SKU'}</p>
          <span
            className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-4 ${
              status === 'published'
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            {status === 'published' ? 'Published to Zoho' : 'Draft'}
          </span>
          <div className="text-3xl font-bold text-blue-600 mb-6">{price}</div>

          {/* Description */}
          <div className="mb-4">
            <div className="text-xs text-gray-600 mb-1">Description</div>
            <div className="text-base text-gray-900">
              {/* Bundle description would go here if schema has it */}
              Complete bundle with all components, packaging, and design elements.
            </div>
          </div>

          {/* Zoho Status */}
          <div className="mb-4">
            <div className="text-xs text-gray-600 mb-1">Zoho Status</div>
            <div className="text-base text-gray-900">
              {zohoStatus}
              {lastSync && ` • Last updated: ${formatRelativeTime(lastSync)}`}
            </div>
          </div>
        </div>

        {/* Components Card */}
        <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Components</h3>
          {components.length === 0 ? (
            <div className="text-sm text-gray-500">No components</div>
          ) : (
            <div className="space-y-4">
              {Array.from(groupedComponents.entries()).map(([categoryName, categoryComponents]) => (
                <div key={categoryName}>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                    {categoryName}
                  </h4>
                  <div className="space-y-2">
                    {categoryComponents.map((component, index) => {
                      const stockItem = component.stockItem
                      const componentName = stockItem?.name || 'Unknown Item'
                      const componentSku = stockItem?.sku || 'No SKU'
                      const quantity = component.quantity || 0
                      const unitPrice = component.price || 0
                      const totalPrice = getComponentTotal(quantity, unitPrice)
                      const componentType = getComponentType()
                      const stockItemId =
                        stockItem && typeof stockItem === 'object'
                          ? ('_id' in stockItem
                              ? (stockItem as any)._id
                              : ('_ref' in stockItem ? (stockItem as any)._ref : undefined))
                          : undefined

                      return (
                        <Link
                          key={component.stockItem?._ref || index}
                          to={stockItemId ? `/stock-items/${stockItemId}` : '#'}
                          className="block bg-gray-50 border border-gray-200 rounded-lg p-3 flex justify-between items-center hover:bg-gray-100 transition-colors touch-target-min-44"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 mb-1 truncate">
                              {componentName}
                            </div>
                            <div className="text-xs text-gray-600">
                              {componentSku} • {componentType} • Qty: {quantity} • {formatPrice(unitPrice)} each
                            </div>
                          </div>
                          <div className="font-semibold text-blue-600 ml-4 flex-shrink-0">
                            {formatPrice(totalPrice)}
                          </div>
                        </Link>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Zoho Stock Level Card (only for published bundles) */}
        {isPublished && (
          <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Zoho Stock Level</h3>
            <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <div className="text-xs text-gray-600 mb-2">Current Stock</div>
                <div
                  className={`text-3xl font-bold ${
                    stockLevel === 0 ? 'text-red-600' : 'text-gray-900'
                  }`}
                >
                  {stockLevel}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-600 mb-2">Reorder Point</div>
                <div className="text-2xl font-bold text-gray-900">{reorderPoint}</div>
              </div>
            </div>
            {stockLevel === 0 && (
              <div className="bg-red-50 border border-red-600 rounded-md p-3 mb-3">
                <div className="text-sm text-red-800 font-medium mb-1">⚠️ Out of Stock</div>
                {lastSync && (
                  <div className="text-xs text-red-700">
                    Last synced: {formatRelativeTime(lastSync)}
                  </div>
                )}
              </div>
            )}
            <Link
              to={`/bundles/${id}/zoho`}
              className="block w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium text-center touch-target-min-44 hover:bg-blue-700 transition-colors"
            >
              View Stock Details
            </Link>
          </div>
        )}

        {/* Products Using This Bundle Card */}
        <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Products Using This Bundle
          </h3>
          {products.length === 0 ? (
            <div className="text-sm text-gray-500">No products use this bundle</div>
          ) : (
            <>
              <div className="text-sm text-gray-600 mb-3">
                {products.length} {products.length === 1 ? 'product' : 'products'} use this bundle
              </div>
              <div className="space-y-2">
                {products.map((product) => {
                  const channelName =
                    product.channelMapping?.[0]?.channel?.toUpperCase() || 'Unknown'
                  const productPrice = product.rrp ? formatPrice(product.rrp) : 'N/A'

                  return (
                    <Link
                      key={product._id}
                      to={`/products/${product._id}`}
                      className="block bg-gray-50 rounded-md p-3 hover:bg-gray-100 transition-colors touch-target-min-44"
                    >
                      <div className="font-medium text-gray-900 mb-1">{product.title}</div>
                      <div className="text-xs text-gray-600">
                        {channelName} • {productPrice}
                      </div>
                    </Link>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <Link
            to="/bundles"
            className="flex-1 bg-gray-100 text-gray-900 py-3 px-6 rounded-lg font-medium text-center touch-target-min-44 hover:bg-gray-200 transition-colors"
          >
            Back to List
          </Link>
          <button
            className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium touch-target-min-44 hover:bg-blue-700 transition-colors"
            onClick={() => {
              // TODO: Implement publish to Zoho functionality
              toast.loading('Publishing to Zoho...')
            }}
          >
            Publish to Zoho
          </button>
        </div>
      </main>
    </div>
  )
}
